import React, { useState, useEffect, useRef } from 'react';
import { Button, Spin, message, Tooltip } from 'antd';
import { EyeOutlined, CloseOutlined, LeftOutlined, RightOutlined, WarningOutlined } from '@ant-design/icons';
import { useAuth0 } from '@auth0/auth0-react';
import MedicalConditionCategoricalView from './MedicalConditionCategoricalView';
import PdfViewer, { Highlight } from './PdfViewer';
import { MedicalCondition, Icd10Code, TextSnippet } from '../../types/codeIdentifier';

// Declare global constants injected by Vite define
declare const API_BASE_URL: string;

interface CategoricalAnalysisWithPdfProps {
  finalEvaluation: MedicalCondition[];
  loadingFinalEvaluation: boolean;
  s3Keys?: string[]; // S3 keys for the uploaded PDFs
}

const CategoricalAnalysisWithPdf: React.FC<CategoricalAnalysisWithPdfProps> = ({
  finalEvaluation,
  loadingFinalEvaluation,
  s3Keys = []
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const [highlights, setHighlights] = useState<Highlight[]>([]);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [loadingHighlights, setLoadingHighlights] = useState(false);
  const [fallbackS3Keys, setFallbackS3Keys] = useState<string[]>([]);
  const [loadingFallback, setLoadingFallback] = useState(false);
  const [activeCode, setActiveCode] = useState<string | null>(null);
  const [currentHighlightIndex, setCurrentHighlightIndex] = useState(0);
  const [pendingAutoScroll, setPendingAutoScroll] = useState(false);
  
  // Modal position and size state - positioned to be fully visible
  const [modalPosition, setModalPosition] = useState({ x: 50, y: 50 });
  const [modalSize, setModalSize] = useState({ width: 800, height: 600 });
  const [minPdfSize, setMinPdfSize] = useState({ width: 400, height: 300 }); // Minimum size based on PDF content
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const modalRef = useRef<HTMLDivElement>(null);
  const pdfViewerRef = useRef<any>(null);

  // Get the first PDF S3 key (assuming single PDF for now)
  const primaryS3Key = s3Keys[0] || fallbackS3Keys[0];

  useEffect(() => {
    if (showPdfModal && primaryS3Key) {
      loadPdfUrl();
    }
  }, [showPdfModal, primaryS3Key]);

  // Center modal when it opens to ensure it's fully visible
  useEffect(() => {
    if (showPdfModal) {
      const centerX = (window.innerWidth - modalSize.width) / 2;
      const centerY = (window.innerHeight - modalSize.height) / 2;
      
      setModalPosition({
        x: Math.max(0, centerX),
        y: Math.max(0, centerY)
      });
    }
  }, [showPdfModal, modalSize.width, modalSize.height]);

  // Mouse event handlers for dragging
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        // Calculate new position with viewport constraints
        const newX = e.clientX - dragStart.x;
        const newY = e.clientY - dragStart.y;
        
        // Ensure modal stays within viewport bounds
        const maxX = window.innerWidth - modalSize.width;
        const maxY = window.innerHeight - modalSize.height;
        
        setModalPosition({
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY))
        });
      } else if (isResizing) {
        const newWidth = Math.max(minPdfSize.width, resizeStart.width + (e.clientX - resizeStart.x));
        const newHeight = Math.max(minPdfSize.height, resizeStart.height + (e.clientY - resizeStart.y));
        setModalSize({ width: newWidth, height: newHeight });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, dragStart, resizeStart, modalSize, modalPosition, minPdfSize]);

  // Load fallback S3 keys if not available in result
  useEffect(() => {
    if (s3Keys.length === 0 && !loadingFallback) {
      loadFallbackS3Keys();
    }
  }, [s3Keys.length, loadingFallback]);

  const loadFallbackS3Keys = async () => {
    setLoadingFallback(true);
    try {
      const token = await getAccessTokenSilently();

      const response = await fetch(`${API_BASE_URL}/api/find-pdf-files`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const pdfFiles = data.pdf_files || [];
        const s3Keys = pdfFiles.map((file: any) => file.s3_key);
        setFallbackS3Keys(s3Keys);
      }
    } catch (error) {
      console.error('Error loading fallback S3 keys:', error);
    } finally {
      setLoadingFallback(false);
    }
  };

  const loadPdfUrl = async () => {
    if (!primaryS3Key) return;

    setLoadingPdf(true);

    try {
      const token = await getAccessTokenSilently();
      const encodedS3Key = encodeURIComponent(primaryS3Key);
      
      // Get PDF URL only - no highlights yet
      const pdfResponse = await fetch(`${API_BASE_URL}/api/pdf-url/${encodedS3Key}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!pdfResponse.ok) {
        throw new Error('Failed to get PDF URL');
      }

      const pdfData = await pdfResponse.json();
      console.log('PDF URL received:', pdfData.url);
      setPdfUrl(pdfData.url);
    } catch (error) {
      console.error('Error loading PDF:', error);
      message.error('Failed to load PDF viewer');
      setShowPdfModal(false);
    } finally {
      setLoadingPdf(false);
    }
  };

  const handleHighlightClick = (highlight: Highlight) => {
    console.log('Highlight clicked:', highlight);
    // You can add additional logic here if needed
  };

  const handlePdfLoad = () => {
    console.log('PDF loaded, checking for pending auto-scroll');
    // If there's a pending auto-scroll and we have highlights, navigate to the first one
    if (pendingAutoScroll && highlights.length > 0) {
      console.log('Auto-scrolling to first highlight');
      setTimeout(() => {
        if (pdfViewerRef.current) {
          pdfViewerRef.current.goToHighlight(highlights[0]);
        }
        setPendingAutoScroll(false);
      }, 500); // Give the PDF a moment to fully render
    }
  };

  // Helper function to check if snippets have valid geometry
  const hasValidGeometry = (snippets: TextSnippet[]): boolean => {
    return snippets.some(s => 
      s.geometry && 
      s.geometry.width > 0 && 
      s.geometry.height > 0 &&
      s.block_ids && 
      s.block_ids.length > 0
    );
  };

  const handleCodeHighlight = (code: Icd10Code, snippets: TextSnippet[]) => {
    if (!primaryS3Key) {
      message.warning('No PDF available for highlighting');
      return;
    }

    // Check if snippets have valid geometry
    if (!snippets || snippets.length === 0 || !hasValidGeometry(snippets)) {
      message.error({
        content: (
          <div>
            <WarningOutlined style={{ marginRight: '8px' }} />
            PDF highlighting unavailable for this analysis. 
            <br />
            <small>Clear system cache and re-run analysis to enable this feature.</small>
          </div>
        ),
        duration: 5
      });
      return;
    }

    // Open modal if not already open
    if (!showPdfModal) {
      setShowPdfModal(true);
    }

    // Clear previous highlights if clicking same code again
    if (activeCode === code.code) {
      setHighlights([]);
      setActiveCode(null);
      setCurrentHighlightIndex(0);
      return;
    }

    try {
      setLoadingHighlights(true);
      setActiveCode(code.code);
      setCurrentHighlightIndex(0);
      
      // Transform snippets directly to highlight format (no backend call needed!)
      // Standard letter size page dimensions (most common)
      // The PDF viewer will handle the actual page size internally
      const PAGE_WIDTH = 612;  // 8.5 inches * 72 points/inch
      const PAGE_HEIGHT = 792; // 11 inches * 72 points/inch
      
      const pdfHighlights: Highlight[] = snippets
        .filter(s => hasValidGeometry([s]))
        .map((snippet, index) => {
          // Convert normalized coordinates (0-1 range) to PDF points
          const geometry = snippet.geometry;
          
          return {
            id: `${code.code}-${index}`,
            page: snippet.page + 1, // Convert from 0-based to 1-based page numbering
            bounds: {
              x: geometry.x * PAGE_WIDTH,
              y: geometry.y * PAGE_HEIGHT,
              width: geometry.width * PAGE_WIDTH,
              height: geometry.height * PAGE_HEIGHT
            },
            phrase: snippet.text,
            code: code.code,
            color: '#FFA500' // Orange highlight
          };
        })
        .sort((a, b) => {
          // Sort by page number (lowest to highest), then by y-position on page
          if (a.page !== b.page) {
            return a.page - b.page;
          }
          return a.bounds.y - b.bounds.y;
        });

      console.log('Client-side transformed highlights (sorted by page):', pdfHighlights);
      setHighlights(pdfHighlights);
      
      if (pdfHighlights.length === 0) {
        message.info(`No valid highlights found for ${code.code}`);
        setActiveCode(null);
      } else {
        message.success(`Found ${pdfHighlights.length} highlight${pdfHighlights.length > 1 ? 's' : ''} for ${code.code}`);
        
        // Auto-navigate to the first highlight
        if (showPdfModal && pdfUrl) {
          // PDF is already loaded, navigate immediately
          setTimeout(() => {
            if (pdfViewerRef.current && pdfHighlights.length > 0) {
              pdfViewerRef.current.goToHighlight(pdfHighlights[0]);
            }
          }, 300);
        } else {
          // PDF not loaded yet, set flag for auto-scroll after load
          setPendingAutoScroll(true);
        }
      }
    } catch (error) {
      console.error('Error creating highlights:', error);
      message.error('Failed to create highlights');
      setActiveCode(null);
    } finally {
      setLoadingHighlights(false);
    }
  };

  const togglePdfModal = () => {
    if (showPdfModal) {
      // Closing modal
      setShowPdfModal(false);
      setPdfUrl('');
      setHighlights([]);
      setActiveCode(null);
      setCurrentHighlightIndex(0);
      setLoadingPdf(false);
      setLoadingHighlights(false);
      setPendingAutoScroll(false);
    } else {
      // Opening modal
      setShowPdfModal(true);
      // loadPdfUrl will be called by useEffect
    }
  };

  const goToNextHighlight = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (highlights.length === 0) return;
    const nextIndex = (currentHighlightIndex + 1) % highlights.length;
    setCurrentHighlightIndex(nextIndex);
    console.log(`Going to next highlight: ${nextIndex + 1} of ${highlights.length}`);
    if (pdfViewerRef.current) {
      pdfViewerRef.current.goToHighlight(highlights[nextIndex]);
    }
  };

  const goToPreviousHighlight = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (highlights.length === 0) return;
    const prevIndex = currentHighlightIndex === 0 ? highlights.length - 1 : currentHighlightIndex - 1;
    setCurrentHighlightIndex(prevIndex);
    console.log(`Going to previous highlight: ${prevIndex + 1} of ${highlights.length}`);
    if (pdfViewerRef.current) {
      pdfViewerRef.current.goToHighlight(highlights[prevIndex]);
    }
  };

  const handleDragStart = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    // Don't start dragging if clicking on a button or interactive element
    if (target.closest('button') || target.closest('a')) {
      return;
    }
    if (target.closest('.pdf-modal-header')) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - modalPosition.x,
        y: e.clientY - modalPosition.y
      });
    }
  };

  const handleResizeStart = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: modalSize.width,
      height: modalSize.height
    });
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* Toggle button for PDF modal */}
      {primaryS3Key ? (
        <div style={{ marginBottom: '16px' }}>
          <Button
            type="primary"
            icon={<EyeOutlined />}
            onClick={togglePdfModal}
            loading={loadingPdf}
          >
            {showPdfModal ? 'Hide PDF Viewer' : 'Show PDF Viewer'}
          </Button>
        </div>
      ) : loadingFallback ? (
        <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f6f6f6', borderRadius: '6px', color: '#666' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Spin size="small" />
            <small>Looking for PDF files...</small>
          </div>
        </div>
      ) : fallbackS3Keys.length > 0 ? (
        <div style={{ marginBottom: '16px' }}>
          <Button
            type="primary"
            icon={<EyeOutlined />}
            onClick={togglePdfModal}
            loading={loadingPdf}
          >
            {showPdfModal ? 'Hide PDF Viewer' : `Show PDF Viewer (Found ${fallbackS3Keys.length} file${fallbackS3Keys.length > 1 ? 's' : ''})`}
          </Button>
        </div>
      ) : (
        <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#fff7e6', borderRadius: '6px', color: '#d46b08', border: '1px solid #ffd591' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <EyeOutlined style={{ color: '#d46b08' }} />
            <strong>PDF highlighting not available</strong>
          </div>
          <small>
            No PDF files found for this analysis. 
            To use the PDF highlighting feature, please upload and process new files.
          </small>
        </div>
      )}

      {/* Floating PDF Modal */}
      {showPdfModal && (
        <div
          ref={modalRef}
          style={{
            position: 'fixed',
            left: modalPosition.x,
            top: modalPosition.y,
            width: modalSize.width,
            height: modalSize.height,
            backgroundColor: '#fff',
            borderRadius: '8px',
            boxShadow: '0 4px 24px rgba(0, 0, 0, 0.4)',
            border: '1px solid #d9d9d9',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
          onMouseDown={handleDragStart}
        >
          {/* Modal Header - Draggable */}
          <div
            className="pdf-modal-header"
            style={{
              padding: '12px 16px',
              borderBottom: '1px solid #f0f0f0',
              backgroundColor: '#fafafa',
              cursor: isDragging ? 'grabbing' : 'grab',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              userSelect: 'none',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <h3 style={{ margin: 0, fontSize: '16px' }}>PDF Viewer</h3>
              {loadingHighlights && (
                <span style={{ color: '#1890ff', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                  <Spin size="small" />
                  Loading highlights...
                </span>
              )}
              {activeCode && !loadingHighlights && highlights.length > 0 && (
                <>
                  <span style={{ 
                    color: '#52c41a', 
                    fontSize: '12px',
                    backgroundColor: '#f6ffed',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    border: '1px solid #b7eb8f'
                  }}>
                    <strong>{activeCode}</strong>
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Button
                      icon={<LeftOutlined />}
                      onClick={goToPreviousHighlight}
                      size="small"
                      disabled={highlights.length <= 1}
                    />
                    <span style={{ fontSize: '12px', color: '#666', minWidth: '60px', textAlign: 'center' }}>
                      {currentHighlightIndex + 1} / {highlights.length}
                    </span>
                    <Button
                      icon={<RightOutlined />}
                      onClick={goToNextHighlight}
                      size="small"
                      disabled={highlights.length <= 1}
                    />
                  </div>
                </>
              )}
            </div>
            <Button
              icon={<CloseOutlined />}
              onClick={togglePdfModal}
              type="text"
              size="small"
            />
          </div>

            {/* Modal Content */}
            <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
              {loadingPdf ? (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%'
                }}>
                  <Spin size="large" />
                </div>
              ) : pdfUrl ? (
                <>
                  <div style={{ padding: '8px 12px', backgroundColor: '#f0f0f0', fontSize: '11px', borderBottom: '1px solid #d9d9d9' }}>
                    Open in new tab: <a href={pdfUrl} target="_blank" rel="noreferrer">link</a>
                  </div>
                  <div style={{ height: 'calc(100% - 32px)' }}>
                    <PdfViewer
                      ref={pdfViewerRef}
                      fileUrl={pdfUrl}
                      highlights={highlights}
                      currentHighlightIndex={currentHighlightIndex}
                      onHighlightClick={handleHighlightClick}
                      onDocumentLoad={handlePdfLoad}
                      onContentSizeChange={(width, height) => {
                        // Add modal header height (~60px) to the minimum height
                        const headerHeight = 60;
                        const minWidth = width;
                        const minHeight = height + headerHeight;
                        setMinPdfSize({ width: minWidth, height: minHeight });
                        // Ensure current modal size is at least the minimum
                        setModalSize(prev => ({
                          width: Math.max(prev.width, minWidth),
                          height: Math.max(prev.height, minHeight)
                        }));
                      }}
                    />
                  </div>
                </>
              ) : (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  color: '#999'
                }}>
                  No PDF available
                </div>
              )}
            </div>

            {/* Resize Handle */}
            <div
              onMouseDown={handleResizeStart}
              style={{
                position: 'absolute',
                right: 0,
                bottom: 0,
                width: '20px',
                height: '20px',
                cursor: 'nwse-resize',
                backgroundColor: 'transparent',
              }}
            >
              <div style={{
                position: 'absolute',
                right: '4px',
                bottom: '4px',
                width: '12px',
                height: '12px',
                borderRight: '2px solid #999',
                borderBottom: '2px solid #999',
              }} />
            </div>
          </div>
      )}

      {/* Categorical view */}
      <MedicalConditionCategoricalView
        finalEvaluation={finalEvaluation}
        loadingFinalEvaluation={loadingFinalEvaluation}
        onHighlightCode={handleCodeHighlight}
        activeHighlightCode={activeCode}
      />
    </div>
  );
};

export default CategoricalAnalysisWithPdf;
